import React, { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { API_BASE } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import PasswordFields from '../components/PasswordFields';
import FormField from '../components/FormField';
import { analyzePassword, passwordsMatch } from '../utils/passwordUtils';
import {
  validateBusinessStep,
  validateContactStep,
  validateDisplayName,
  validateBusinessName,
  validatePhone,
  validateEmail,
} from '../utils/formValidation';

const STEPS = [
  { id: 'business', title: 'Your business' },
  { id: 'contact', title: 'Contact info' },
  { id: 'account', title: 'Create account' },
  { id: 'review', title: 'Review' },
];

const BUSINESS_FIELDS = ['display_name', 'business_name'];
const CONTACT_FIELDS = ['phone', 'email'];

const Apply = () => {
  const { isAuthenticated, setSession } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    display_name: '',
    business_name: '',
    phone: '',
    email: '',
    password: '',
    password_confirm: '',
  });
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  const businessValidation = useMemo(() => validateBusinessStep(form), [form]);
  const contactValidation = useMemo(() => validateContactStep(form), [form]);
  const passwordAnalysis = useMemo(
    () => analyzePassword(form.password, form.email),
    [form.password, form.email]
  );
  const passwordReady =
    passwordAnalysis.isValid && passwordsMatch(form.password, form.password_confirm);

  const stepValid = [businessValidation.valid, contactValidation.valid, passwordReady, true][step];

  if (isAuthenticated) return <Navigate to="/onboarding" replace />;

  const touch = (field) => setTouched((t) => ({ ...t, [field]: true }));
  const touchFields = (fields) =>
    setTouched((t) => fields.reduce((acc, f) => ({ ...acc, [f]: true }), { ...t }));

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handlePhoneBlur = () => {
    touch('phone');
    const result = validatePhone(form.phone);
    if (result.valid) setField('phone', result.value);
  };

  const handleEmailBlur = () => {
    touch('email');
    const result = validateEmail(form.email);
    if (result.valid) setField('email', result.value);
  };

  const validateCurrentStep = () => {
    if (step === 0) {
      touchFields(BUSINESS_FIELDS);
      if (!businessValidation.valid) {
        const first = BUSINESS_FIELDS.find((f) => !businessValidation.fields[f]?.valid);
        toast.error(businessValidation.fields[first]?.error || 'Fix the highlighted fields');
        return false;
      }
    }
    if (step === 1) {
      touchFields(CONTACT_FIELDS);
      if (!contactValidation.valid) {
        const first = CONTACT_FIELDS.find((f) => !contactValidation.fields[f]?.valid);
        toast.error(contactValidation.fields[first]?.error || 'Fix the highlighted fields');
        return false;
      }
    }
    if (step === 2) {
      if (!passwordAnalysis.isValid) {
        toast.error('Please meet all password requirements');
        return false;
      }
      if (!passwordsMatch(form.password, form.password_confirm)) {
        toast.error('Passwords do not match');
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateCurrentStep()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!businessValidation.valid || !contactValidation.valid || !passwordReady) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        display_name: businessValidation.fields.display_name.value,
        business_name: businessValidation.fields.business_name.value || null,
        phone: contactValidation.fields.phone.value,
        email: contactValidation.fields.email.value,
        password: form.password,
      };

      const res = await fetch(`${API_BASE}/vendor/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Application failed');

      const loginRes = await fetch(`${API_BASE}/auth/vendor/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: payload.email, password: payload.password }),
      });
      const loginData = await loginRes.json();
      if (!loginRes.ok) throw new Error(loginData.message || 'Login after apply failed');

      setSession(loginData.data.token, loginData.data.user);
      toast.success('Account created — next up: your documents');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const displayResult = validateDisplayName(form.display_name);
  const businessResult = validateBusinessName(form.business_name);
  const phoneResult = validatePhone(form.phone);
  const emailResult = validateEmail(form.email);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-md w-full max-w-lg p-8">
        <div className="mb-8">
          <p className="text-xs font-medium uppercase tracking-wide text-teal-700">
            Step {step + 1} of {STEPS.length}
          </p>
          <div className="flex gap-1.5 mt-3">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? 'bg-teal-600' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        {step === 0 && (
          <div>
            <h1 className="text-2xl font-semibold text-teal-800">Tell us about your business</h1>
            <p className="text-slate-600 text-sm mt-2 mb-6">
              This is how we&apos;ll list you to customers. You can add licenses and insurance on the next screens.
            </p>
            <FormField
              id="display_name"
              label="Business name"
              hint="Shown to customers on job assignments"
              value={form.display_name}
              onChange={(e) => setField('display_name', e.target.value)}
              onBlur={() => touch('display_name')}
              placeholder="e.g. Sparkle Clean Co."
              autoFocus
              maxLength={100}
              error={displayResult.error}
              valid={displayResult.valid}
              touched={touched.display_name}
              autoComplete="organization"
            />
            <FormField
              id="business_name"
              label="Legal business name"
              optional
              value={form.business_name}
              onChange={(e) => setField('business_name', e.target.value)}
              onBlur={() => touch('business_name')}
              placeholder="If different from the name above"
              maxLength={150}
              error={businessResult.error}
              valid={businessResult.valid && Boolean(form.business_name.trim())}
              touched={touched.business_name}
              autoComplete="organization"
            />
          </div>
        )}

        {step === 1 && (
          <div>
            <h1 className="text-2xl font-semibold text-teal-800">How can we reach you?</h1>
            <p className="text-slate-600 text-sm mt-2 mb-6">
              We&apos;ll use this for job notifications and onboarding updates.
            </p>
            <FormField
              id="phone"
              label="Phone"
              type="tel"
              hint="US number — we'll format it automatically"
              value={form.phone}
              onChange={(e) => setField('phone', e.target.value)}
              onBlur={handlePhoneBlur}
              placeholder="(555) 555-0100"
              autoFocus
              inputMode="tel"
              autoComplete="tel"
              error={phoneResult.error}
              valid={phoneResult.valid}
              touched={touched.phone}
            />
            <FormField
              id="email"
              label="Email"
              type="email"
              hint="Used for login and account notifications"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              onBlur={handleEmailBlur}
              placeholder="you@yourbusiness.com"
              maxLength={255}
              autoComplete="email"
              error={emailResult.error}
              valid={emailResult.valid}
              touched={touched.email}
            />
          </div>
        )}

        {step === 2 && (
          <div>
            <h1 className="text-2xl font-semibold text-teal-800">Create your login</h1>
            <p className="text-slate-600 text-sm mt-2 mb-6">
              You&apos;ll use this email and password to sign in, upload documents, and manage jobs.
            </p>
            <p className="text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-2 mb-5">
              {contactValidation.fields.email.value || form.email}
            </p>
            <PasswordFields
              password={form.password}
              confirmPassword={form.password_confirm}
              email={form.email}
              onPasswordChange={(value) => setField('password', value)}
              onConfirmChange={(value) => setField('password_confirm', value)}
              autoFocus
            />
          </div>
        )}

        {step === 3 && (
          <div>
            <h1 className="text-2xl font-semibold text-teal-800">Review and continue</h1>
            <p className="text-slate-600 text-sm mt-2 mb-6">
              After this, you&apos;ll upload your COI, license, and sign the vendor agreement.
            </p>
            <dl className="divide-y divide-slate-100 text-sm">
              <div className="py-3 flex justify-between gap-4">
                <dt className="text-slate-500 shrink-0">Business name</dt>
                <dd className="font-medium text-slate-800 text-right">
                  {businessValidation.fields.display_name.value}
                </dd>
              </div>
              {businessValidation.fields.business_name.value && (
                <div className="py-3 flex justify-between gap-4">
                  <dt className="text-slate-500 shrink-0">Legal name</dt>
                  <dd className="font-medium text-slate-800 text-right">
                    {businessValidation.fields.business_name.value}
                  </dd>
                </div>
              )}
              <div className="py-3 flex justify-between gap-4">
                <dt className="text-slate-500 shrink-0">Phone</dt>
                <dd className="font-medium text-slate-800 text-right">
                  {contactValidation.fields.phone.value}
                </dd>
              </div>
              <div className="py-3 flex justify-between gap-4">
                <dt className="text-slate-500 shrink-0">Email</dt>
                <dd className="font-medium text-slate-800 text-right">
                  {contactValidation.fields.email.value}
                </dd>
              </div>
            </dl>
          </div>
        )}

        <div className="flex gap-3 mt-8">
          {step > 0 && (
            <button
              type="button"
              onClick={back}
              disabled={loading}
              className="flex-1 border border-slate-300 text-slate-700 py-2.5 rounded-lg hover:bg-slate-50 disabled:opacity-50 text-sm font-medium"
            >
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              disabled={!stepValid}
              className="flex-1 bg-teal-700 text-white py-2.5 rounded-lg hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={loading}
              className="flex-1 bg-teal-700 text-white py-2.5 rounded-lg hover:bg-teal-800 disabled:opacity-50 text-sm font-medium"
            >
              {loading ? 'Creating account…' : 'Create account & continue'}
            </button>
          )}
        </div>

        <p className="text-sm text-slate-600 mt-6 text-center">
          Already have an account?{' '}
          <Link to="/login" className="text-teal-700 font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Apply;
